import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  getProfiles,
  getResources,
} from "../services/api/maintenance/permissionCatalog.api";
import { compareStrings } from "../utils/compareStrings";

/**
 * Loads the full Profile and Resource catalogs once (neither is paginated, and each already
 * embeds its inverse relation - profile.resources / resource.profiles) and derives name-keyed
 * maps so views can resolve either relation locally instead of making per-selection requests.
 * @returns {{
 *   profiles: object[],
 *   profilesByName: Map<string, object>,
 *   resourcesByName: Map<string, object>,
 *   resources: object[],
 *   loading: boolean,
 *   error: Error | null,
 *   reload: () => void,
 *   patchProfileResources: (profileName: string, resourceNames: string[]) => void,
 * }}
 */
export const usePermissionCatalog = () => {
  const [profiles, setProfiles] = useState([]);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    const source = axios.CancelToken.source();
    setLoading(true);
    setError(null);

    Promise.all([
      getProfiles({ cancelToken: source.token }),
      getResources({ cancelToken: source.token }),
    ])
      .then(([profilesResponse, resourcesResponse]) => {
        setProfiles(profilesResponse ?? []);
        setResources(resourcesResponse ?? []);
      })
      .catch((requestError) => {
        if (axios.isCancel(requestError)) return;
        setError(requestError);
      })
      .finally(() => setLoading(false));

    return () => source.cancel();
  }, []);

  useEffect(() => load(), [load]);

  const profilesByName = useMemo(
    () => new Map(profiles.map((profile) => [profile.name, profile])),
    [profiles]
  );

  const resourcesByName = useMemo(
    () => new Map(resources.map((resource) => [resource.name, resource])),
    [resources]
  );

  // Applied after a successful associate/disassociate so the affected profile's resource
  // list reflects the server's confirmed state without re-fetching the whole catalog.
  const patchProfileResources = useCallback((profileName, resourceNames) => {
    setProfiles((current) =>
      current.map((profile) =>
        profile.name === profileName
          ? { ...profile, resources: resourceNames }
          : profile
      )
    );
  }, []);

  return {
    profiles,
    profilesByName,
    resourcesByName,
    resources,
    loading,
    error,
    reload: load,
    patchProfileResources,
  };
};

/**
 * Resources from the full catalog that a given profile does not yet have - the option list for
 * the "add resource to profile" selector.
 * @param {{ resources: string[] }} profile
 * @param {{ name: string }[]} resources
 */
export const getUnassociatedResources = (profile, resources) => {
  const associatedNames = new Set(profile?.resources ?? []);
  return resources
    .filter((resource) => !associatedNames.has(resource.name))
    .sort((a, b) => compareStrings(a.name, b.name));
};
